import { Box, Group, Stack, Text } from "@mantine/core"
import { ReactNode } from "react"
import { useTranslation } from "react-i18next"
import useSWR from "swr"
import { DeliveryApi, VehicleApi } from "../../apis"
import { renderFullName } from "../../utils/render-name"
import classes from "./pending.module.css"

function SideCard({ title, count, children }: Readonly<{ title: string; count?: ReactNode; children: ReactNode }>) {
  return (
    <Box className={classes.sideCard}>
      <Group justify="space-between" mb="xs" wrap="nowrap">
        <Text fw={600} fz="var(--nw-fz-body)">
          {title}
        </Text>
        {count != null && (
          <Text ff="monospace" fw={600} fz="var(--nw-fz-mono)">
            {count}
          </Text>
        )}
      </Group>
      {children}
    </Box>
  )
}

interface StaffSelectRow {
  id: string
  lastName?: string | null
  firstName?: string | null
}

function DriverPool() {
  const { t } = useTranslation("delivery")
  const { data, isLoading } = useSWR<StaffSelectRow[]>(
    "pending.side.drivers",
    async () => {
      const result = await DeliveryApi.staffSelect({ limit: 20 })
      return Array.isArray(result) ? result : (result?.rows ?? [])
    },
    { revalidateOnFocus: false },
  )
  const rows = data ?? []
  return (
    <SideCard title={t("manage.side.drivers")} count={isLoading ? "-" : rows.length}>
      {rows.length === 0 ? (
        <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)">
          {isLoading ? t("manage.state.loading") : t("manage.side.noDrivers")}
        </Text>
      ) : (
        <Stack gap={4}>
          {rows.map((staff) => (
            <Text key={staff.id} fz="var(--nw-fz-body)" truncate>
              {renderFullName(staff)}
            </Text>
          ))}
        </Stack>
      )}
    </SideCard>
  )
}

function VehiclePool() {
  const { t } = useTranslation("delivery")
  const { data, isLoading } = useSWR(
    "pending.side.vehicles",
    () => VehicleApi.list({ filter: { isActive: true }, offset: { limit: 20, skip: 0 } }),
    { revalidateOnFocus: false },
  )
  const rows = data?.rows ?? []
  return (
    <SideCard title={t("manage.side.vehicles")} count={isLoading ? "-" : `${rows.length} / ${data?.count ?? 0}`}>
      {rows.length === 0 ? (
        <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)">
          {isLoading ? t("manage.state.loading") : t("manage.side.noVehicles")}
        </Text>
      ) : (
        <Stack gap="xs">
          {rows.map((v) => (
            <Box key={v.id}>
              <Text fz="var(--nw-fz-body)">
                <Text span ff="monospace" fw={600} mr={6}>
                  {v.plateNumber}
                </Text>
                {[v.make, v.model].filter(Boolean).join(" ")}
              </Text>
              {v.vehicleClass && (
                <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)">
                  {t(`vehicle.class.${v.vehicleClass}`, { defaultValue: "-" })}
                </Text>
              )}
            </Box>
          ))}
        </Stack>
      )}
    </SideCard>
  )
}

interface Props {
  avgWaitHours: number
  slaRiskCount: number
  bigFreightCount: number
}

/** Right column (canvas Ord_DeliveryHub): drivers, vehicles, today's figures. */
export function PendingSidePanel({ avgWaitHours, slaRiskCount, bigFreightCount }: Props) {
  const { t } = useTranslation("delivery")
  const avgWait =
    avgWaitHours < 1
      ? t("manage.pending.minutes", { n: Math.round(avgWaitHours * 60) })
      : t("manage.pending.hours", { n: avgWaitHours.toFixed(1) })
  const stats = [
    { key: "wait", label: t("manage.side.avgWait"), value: avgWait },
    { key: "sla", label: t("manage.side.slaRisk"), value: slaRiskCount },
    { key: "capacity", label: t("manage.side.capacityMismatch"), value: bigFreightCount },
  ]
  return (
    <Stack gap="md">
      <DriverPool />
      <VehiclePool />
      <SideCard title={t("manage.side.today")}>
        <Stack gap={6}>
          {stats.map((s) => (
            <Group key={s.key} justify="space-between" wrap="nowrap">
              <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)">
                {s.label}
              </Text>
              <Text ff="monospace" fz="var(--nw-fz-mono)">
                {s.value}
              </Text>
            </Group>
          ))}
        </Stack>
      </SideCard>
    </Stack>
  )
}
